import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Save, Plus, Edit2, Trash2, RotateCcw,
  Truck, Calculator, TrendingDown, CheckSquare,
  RefreshCw, AlertCircle
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/SupplierInvoice.css';

/* ── Pure helpers defined OUTSIDE component to prevent stale closures ── */
const EMPTY_FORM = () => ({
  InvoiceNo: '', OurReferenceNo: '',
  InvoiceDate: new Date().toISOString().slice(0, 10),
  DueDate: '', SupplierID: '', CurrencyID: '', ExchangeRate: 1,
  PaymentTerms: 'net30', ReferenceNo: '', Notes: '',
  SubTotal: 0, TaxPercent: 0, TaxAmount: 0,
  DiscountPercent: 0, DiscountAmount: 0, TotalAmount: 0, Status: 'Pending',
});

const EMPTY_ITEM = () => ({
  ItemID: '', Description: '', Quantity: 1,
  UnitPrice: 0, TaxPercent: 0, DiscountPercent: 0, Amount: 0,
});

const PAYMENT_TERMS = [
  { id: 'immediate', label: 'Immediate'   },
  { id: 'net15',     label: 'Net 15 Days' },
  { id: 'net30',     label: 'Net 30 Days' },
  { id: 'net45',     label: 'Net 45 Days' },
  { id: 'net60',     label: 'Net 60 Days' },
];

const sf  = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const f2  = (v) => sf(v).toFixed(2);

const SupplierInvoiceForm = ({ editData = null, onClose, onSaved }) => {
  const [initLoading,       setInitLoading]       = useState(true);
  const [saving,            setSaving]            = useState(false);
  const [generating,        setGenerating]        = useState(false);
  const [error,             setError]             = useState('');
  const [success,           setSuccess]           = useState('');
  const [suppliers,         setSuppliers]         = useState([]);
  const [currencies,        setCurrencies]        = useState([]);
  const [items,             setItems]             = useState([]);
  const [uninvoiced,        setUninvoiced]        = useState([]);
  const [uninvoicedLoading, setUninvoicedLoading] = useState(false);
  const [uninvoicedError,   setUninvoicedError]   = useState('');
  const [selectedPayments,  setSelectedPayments]  = useState([]);
  const [showSelector,      setShowSelector]      = useState(false);
  const [form,              setForm]              = useState(EMPTY_FORM);
  const [invoiceItems,      setInvoiceItems]      = useState([]);
  const [itemForm,          setItemForm]          = useState(EMPTY_ITEM);
  const [editingItemIdx,    setEditingItemIdx]    = useState(null);

  /* ESC */
  const stableClose = useCallback(() => onClose(), []); // eslint-disable-line
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') stableClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [stableClose]);

  /* init */
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        await Promise.all([
          loadDropdowns(mounted),
          loadUninvoicedInternal(mounted),
        ]);
        if (!mounted) return;
        if (editData) await loadForEdit(editData, mounted);
        else          await generateRefNo(mounted);
      } finally {
        if (mounted) setInitLoading(false);
      }
    };
    init();
    return () => { mounted = false; };
  }, []); // eslint-disable-line

  const loadDropdowns = async (mounted = true) => {
    try {
      const [s, cu, it] = await Promise.all([
        apiClient.get('/Suppliers.php'),
        apiClient.get('/Currencies.php'),
        apiClient.get('/Items.php'),
      ]);
      if (!mounted) return;
      if (s.data?.success)  setSuppliers(s.data.data   || []);
      if (cu.data?.success) setCurrencies(cu.data.data || []);
      if (it.data?.success) setItems(it.data.data      || []);
    } catch (err) {
      console.error('Dropdown load error:', err.message);
    }
  };

  const loadUninvoicedInternal = async (mounted = true) => {
    if (mounted) { setUninvoicedLoading(true); setUninvoicedError(''); }
    try {
      const res = await apiClient.get('/SupplierInvoices.php', { params: { 'uninvoiced-payments': 1 } });
      if (!mounted) return;
      if (res.data?.success) {
        setUninvoiced(res.data.data || []);
      } else {
        setUninvoicedError(res.data?.message || 'Failed to load payments');
      }
    } catch (err) {
      if (!mounted) return;
      setUninvoicedError(err.response?.data?.message || err.message || 'Failed to load uninvoiced payments');
    } finally {
      if (mounted) setUninvoicedLoading(false);
    }
  };

  const loadUninvoiced = () => loadUninvoicedInternal(true);

  const generateRefNo = async (mounted = true) => {
    try {
      const res = await apiClient.get('/SupplierInvoices.php', { params: { 'next-number': 1 } });
      if (!mounted) return;
      if (res.data?.success && res.data.data?.referenceNo) {
        setForm(p => ({ ...p, OurReferenceNo: res.data.data.referenceNo }));
      }
    } catch {
      if (mounted) setForm(p => ({ ...p, OurReferenceNo: `SINV-${String(Date.now()).slice(-6)}` }));
    }
  };

  const loadForEdit = async (inv, mounted = true) => {
    let full = inv;
    if (!inv.Items) {
      try {
        const res = await apiClient.get('/SupplierInvoices.php', { params: { id: inv.SupplierInvoiceID } });
        if (res.data?.success) full = res.data.data;
      } catch (err) { console.error('Load for edit error:', err.message); }
    }
    if (!mounted) return;
    setForm({
      InvoiceNo:       full.InvoiceNo       || '',
      OurReferenceNo:  full.OurReferenceNo  || '',
      InvoiceDate:     (full.InvoiceDate    || '').slice(0, 10),
      DueDate:         (full.DueDate        || '').slice(0, 10),
      SupplierID:      full.SupplierID      || '',
      CurrencyID:      full.CurrencyID      || '',
      ExchangeRate:    full.ExchangeRate    || 1,
      PaymentTerms:    full.PaymentTerms    || 'net30',
      ReferenceNo:     full.ReferenceNo     || '',
      Notes:           full.Notes           || '',
      SubTotal:        full.SubTotal        || 0,
      TaxPercent:      full.TaxPercent      || 0,
      TaxAmount:       full.TaxAmount       || 0,
      DiscountPercent: full.DiscountPercent || 0,
      DiscountAmount:  full.DiscountAmount  || 0,
      TotalAmount:     full.TotalAmount     || 0,
      Status:          full.Status          || 'Pending',
    });
    setInvoiceItems((full.Items || []).map(item => ({
      ...item,
      Quantity:  sf(item.Quantity),
      UnitPrice: sf(item.UnitPrice),
      Amount:    sf(item.Amount),
    })));
  };

  /* recalc */
  const recalc = (f, lines) => {
    const sub  = lines.reduce((s, l) => s + sf(l.Amount), 0);
    const disc = sub * (sf(f.DiscountPercent) / 100);
    const aft  = sub - disc;
    const tax  = aft * (sf(f.TaxPercent) / 100);
    setForm(p => ({ ...p, SubTotal: f2(sub), DiscountAmount: f2(disc), TaxAmount: f2(tax), TotalAmount: f2(aft + tax) }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(p => {
      const u = { ...p, [name]: value };
      if (name === 'TaxPercent' || name === 'DiscountPercent') {
        setInvoiceItems(lines => { recalc(u, lines); return lines; });
      }
      return u;
    });
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemForm(p => {
      const u = { ...p, [name]: value };
      if (name === 'ItemID') {
        const sel = items.find(i => String(i.itemid) === String(value));
        if (sel) { u.Description = sel.itemname || ''; u.UnitPrice = sel.unitprice || 0; }
      }
      const qty  = sf(name === 'Quantity'        ? value : u.Quantity);
      const pri  = sf(name === 'UnitPrice'       ? value : u.UnitPrice);
      const tax  = sf(name === 'TaxPercent'      ? value : u.TaxPercent);
      const disc = sf(name === 'DiscountPercent' ? value : u.DiscountPercent);
      const base  = qty * pri;
      const after = base - base * (disc / 100);
      u.Amount = f2(after + after * (tax / 100));
      return u;
    });
  };

  const handleAddItem = () => {
    if (!String(itemForm.Description || '').trim()) { setError('Please enter item description'); return; }
    setError('');
    setInvoiceItems(prev => {
      const updated = editingItemIdx !== null
        ? prev.map((it, i) => i === editingItemIdx ? { ...itemForm } : it)
        : [...prev, { ...itemForm }];
      recalc(form, updated);
      return updated;
    });
    setItemForm(EMPTY_ITEM());
    setEditingItemIdx(null);
  };

  const handleEditItem   = (i) => { setItemForm({ ...invoiceItems[i] }); setEditingItemIdx(i); };
  const handleDeleteItem = (i) => {
    setInvoiceItems(prev => { const u = prev.filter((_, idx) => idx !== i); recalc(form, u); return u; });
  };
  const handleCancelItem = () => { setItemForm(EMPTY_ITEM()); setEditingItemIdx(null); };

  const handleGenerate = async () => {
    if (!selectedPayments.length) { setError('Please select at least one payment'); return; }
    setGenerating(true); setError(''); setSuccess('');
    try {
      const res = await apiClient.post('/SupplierInvoices.php', {
        _action:         'generate-from-payments',
        PaymentIDs:      selectedPayments,
        InvoiceDate:     form.InvoiceDate,
        DueDate:         form.DueDate || null,
        TaxPercent:      form.TaxPercent,
        DiscountPercent: form.DiscountPercent,
        PaymentTerms:    form.PaymentTerms,
      });
      if (res.data?.success) {
        const count = Array.isArray(res.data.data) ? res.data.data.length : 1;
        setSuccess(`${count} invoice(s) generated successfully!`);
        setSelectedPayments([]);
        setShowSelector(false);
        setTimeout(() => { onSaved(); }, 1200);
      } else {
        setError(res.data?.message || 'Failed to generate invoices');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.SupplierID)                     { setError('Please select a supplier');             return; }
    if (!String(form.InvoiceNo || '').trim()) { setError('Please enter supplier invoice number'); return; }
    if (!invoiceItems.length)                 { setError('Please add at least one line item');    return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = { ...form, Items: invoiceItems, ...(editData ? { SupplierInvoiceID: editData.SupplierInvoiceID } : {}) };
      const res = editData
        ? await apiClient.put('/SupplierInvoices.php', payload)
        : await apiClient.post('/SupplierInvoices.php', payload);
      if (res.data?.success) {
        setSuccess(editData ? `Invoice ${form.OurReferenceNo} updated!` : `Invoice ${res.data.data?.OurReferenceNo || form.OurReferenceNo} created!`);
        setTimeout(() => { onSaved(); }, 900);
      } else {
        throw new Error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setForm(EMPTY_FORM()); setInvoiceItems([]); setItemForm(EMPTY_ITEM());
    setEditingItemIdx(null); setSelectedPayments([]); setError(''); setSuccess('');
    if (!editData) generateRefNo(true);
  };

  const togglePayment = (id) =>
    setSelectedPayments(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  /* ── Loading screen while initialising ── */
  if (initLoading) {
    return (
      <div className="si-modal-overlay">
        <div className="si-modal" style={{ alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="si-spinner" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: '#c2410c', fontWeight: 600, margin: 0 }}>Loading invoice form…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="si-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="si-modal-header">
          <div className="si-modal-header-left">
            <div className="si-modal-icon"><Truck size={26} color="#fff" /></div>
            <div>
              <div className="si-modal-header-row">
                <span className="si-modal-title">{editData ? 'Edit Supplier Invoice' : 'New Supplier Invoice'}</span>
                <span className="si-modal-badge">📋 {form.OurReferenceNo || 'SINV-000001'}</span>
              </div>
            </div>
          </div>
          <button className="si-modal-close" onClick={onClose} title="Close (ESC)"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}>
            <X size={20} color="#fff" />
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="si-modal-toolbar">
          <button className="si-tb-btn si-tb-save" onClick={handleSave} disabled={saving || generating || !invoiceItems.length}>
            <Save size={16} />
            {saving ? 'Saving…' : editData ? 'Update Invoice' : 'Save Invoice'}
          </button>
          <button className="si-tb-btn" onClick={() => { setShowSelector(s => !s); setSelectedPayments([]); setError(''); }} disabled={saving || generating}>
            <CheckSquare size={16} />
            {showSelector ? 'Hide Selector' : 'Generate from Payments'}
          </button>
          <button className="si-tb-btn" onClick={handleClear} disabled={saving || generating}>
            <RotateCcw size={16} /> Clear
          </button>
        </div>

        {/* ALERTS */}
        {error   && <div className="si-alert si-alert-error"><AlertCircle size={15} style={{ flexShrink: 0 }} /><span>{error}</span></div>}
        {success && <div className="si-alert si-alert-success"><span>{success}</span></div>}

        {/* BODY */}
        <div className="si-modal-body">

          {/* Generate from Payments Selector */}
          {showSelector && (
            <div className="si-section si-section-warning">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <h3 className="si-section-title" style={{ marginBottom: 0 }}>
                  <CheckSquare size={16} /> Select Uninvoiced Cost Payments
                </h3>
                <button className="si-tb-btn" onClick={loadUninvoiced} disabled={uninvoicedLoading} style={{ padding:'4px 10px', fontSize:12 }}>
                  <RefreshCw size={13} className={uninvoicedLoading ? 'si-spin' : ''} /> Refresh
                </button>
              </div>

              <div className="si-grid-4" style={{ marginBottom: 16 }}>
                <div className="si-field"><label>Invoice Date</label><input type="date" name="InvoiceDate" value={form.InvoiceDate} onChange={handleFormChange} /></div>
                <div className="si-field"><label>Due Date</label><input type="date" name="DueDate" value={form.DueDate} onChange={handleFormChange} /></div>
                <div className="si-field"><label>Tax %</label><input type="number" step="0.01" name="TaxPercent" value={form.TaxPercent} onChange={handleFormChange} placeholder="0.00" /></div>
                <div className="si-field"><label>Discount %</label><input type="number" step="0.01" name="DiscountPercent" value={form.DiscountPercent} onChange={handleFormChange} placeholder="0.00" /></div>
              </div>

              {uninvoicedLoading ? (
                <div style={{ textAlign:'center', padding:24 }}>
                  <div className="si-spinner" style={{ margin:'0 auto 8px' }} />
                  <span style={{ color:'#777', fontSize:13 }}>Loading cost payments…</span>
                </div>
              ) : uninvoicedError ? (
                <div className="si-alert si-alert-error" style={{ margin:0 }}>
                  <AlertCircle size={14} />
                  <span>{uninvoicedError}</span>
                  <button onClick={loadUninvoiced} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#dc2626', fontSize:12 }}>Retry</button>
                </div>
              ) : uninvoiced.length === 0 ? (
                <p style={{ textAlign:'center', padding:20, color:'#777' }}>No uninvoiced cost payments found</p>
              ) : (
                <>
                  <div className="si-selector-table-wrap">
                    <table className="si-table">
                      <thead>
                        <tr>
                          <th style={{ width:36 }}>
                            <input type="checkbox"
                              checked={selectedPayments.length === uninvoiced.length && uninvoiced.length > 0}
                              onChange={(e) => setSelectedPayments(e.target.checked ? uninvoiced.map(p => p.ConsignmentPaymentID) : [])} />
                          </th>
                          <th>Consignment</th><th>Date</th><th>Supplier</th><th>Item</th><th>Mode</th>
                          <th className="si-th-right">Cost Amt</th><th className="si-th-right">Tax</th><th className="si-th-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uninvoiced.map(p => (
                          <tr key={p.ConsignmentPaymentID}
                            className={`si-tr${selectedPayments.includes(p.ConsignmentPaymentID) ? ' si-tr-selected' : ''}`}
                            onClick={() => togglePayment(p.ConsignmentPaymentID)} style={{ cursor:'pointer' }}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={selectedPayments.includes(p.ConsignmentPaymentID)} onChange={() => togglePayment(p.ConsignmentPaymentID)} />
                            </td>
                            <td className="si-mono">{p.ConsignmentNo || '—'}</td>
                            <td>{p.ConsignmentDate ? new Date(p.ConsignmentDate).toLocaleDateString() : '—'}</td>
                            <td>{p.suppliername || '—'}</td>
                            <td>{p.itemname     || '—'}</td>
                            <td>{p.PaymentMode  || '—'}</td>
                            <td className="si-td-right si-mono">{f2(p.CostAmount)}</td>
                            <td className="si-td-right si-mono">{f2(p.CostTaxAmt)}</td>
                            <td className="si-td-right si-mono si-bold">{f2(p.CostTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {selectedPayments.length > 0 && (
                    <div className="si-selector-actions">
                      <span style={{ fontSize:13, color:'#c2410c', fontWeight:600 }}>{selectedPayments.length} selected</span>
                      <button className="si-tb-btn si-tb-save" onClick={handleGenerate} disabled={generating}>
                        {generating ? <><span className="si-btn-spinner" /> Generating…</> : <><Plus size={15} /> Generate Invoice</>}
                      </button>
                      <button className="si-tb-btn" onClick={() => { setShowSelector(false); setSelectedPayments([]); }}>Cancel</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Invoice Details */}
          <div className="si-section">
            <h3 className="si-section-title"><Truck size={16} /> Invoice Details</h3>
            <div className="si-grid-3">
              <div className="si-field">
                <label>Our Reference No</label>
                <input type="text" value={form.OurReferenceNo} readOnly className="si-readonly" style={{ fontWeight:700, background:'#fff3e0' }} />
              </div>
              <div className="si-field">
                <label>Supplier Invoice No *</label>
                <input type="text" name="InvoiceNo" value={form.InvoiceNo} onChange={handleFormChange} placeholder="Supplier's own invoice number" />
              </div>
              <div className="si-field">
                <label>Invoice Date *</label>
                <input type="date" name="InvoiceDate" value={form.InvoiceDate} onChange={handleFormChange} />
              </div>
              <div className="si-field">
                <label>Due Date</label>
                <input type="date" name="DueDate" value={form.DueDate} onChange={handleFormChange} />
              </div>
              <div className="si-field">
                <label>Supplier *</label>
                <select name="SupplierID" value={form.SupplierID} onChange={handleFormChange}>
                  <option value="">— Select Supplier —</option>
                  {suppliers.map(s => <option key={s.supplierid} value={s.supplierid}>{s.suppliername}{s.suppliercode ? ` (${s.suppliercode})` : ''}</option>)}
                </select>
              </div>
              <div className="si-field">
                <label>Currency</label>
                <select name="CurrencyID" value={form.CurrencyID} onChange={handleFormChange}>
                  <option value="">— Select Currency —</option>
                  {currencies.map(c => <option key={c.currencyid} value={c.currencyid}>{c.currencycode || c.symbol || c.currencyname} – {c.currencyname}</option>)}
                </select>
              </div>
              <div className="si-field">
                <label>Exchange Rate</label>
                <input type="number" step="0.0001" min="0" name="ExchangeRate" value={form.ExchangeRate} onChange={handleFormChange} placeholder="1.0000" />
              </div>
              <div className="si-field">
                <label>Payment Terms</label>
                <select name="PaymentTerms" value={form.PaymentTerms} onChange={handleFormChange}>
                  {PAYMENT_TERMS.map(pt => <option key={pt.id} value={pt.id}>{pt.label}</option>)}
                </select>
              </div>
              <div className="si-field">
                <label>Reference No</label>
                <input type="text" name="ReferenceNo" value={form.ReferenceNo} onChange={handleFormChange} placeholder="PO#, Job#, Contract#…" />
              </div>
              <div className="si-field">
                <label>Status</label>
                <select name="Status" value={form.Status} onChange={handleFormChange}>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Paid">Paid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="si-section">
            <h3 className="si-section-title"><Calculator size={16} /> Invoice Line Items</h3>
            <div className="si-item-entry">
              <div className="si-item-grid">
                <div className="si-field">
                  <label>Item</label>
                  <select name="ItemID" value={itemForm.ItemID} onChange={handleItemChange}>
                    <option value="">— Select —</option>
                    {items.map(it => <option key={it.itemid} value={it.itemid}>{it.itemname}</option>)}
                  </select>
                </div>
                <div className="si-field si-span-2">
                  <label>Description *</label>
                  <input type="text" name="Description" value={itemForm.Description} onChange={handleItemChange} placeholder="Item description" />
                </div>
                <div className="si-field">
                  <label>Qty</label>
                  <input type="number" step="0.01" min="0" name="Quantity" value={itemForm.Quantity} onChange={handleItemChange} />
                </div>
                <div className="si-field">
                  <label>Unit Price</label>
                  <input type="number" step="0.01" min="0" name="UnitPrice" value={itemForm.UnitPrice} onChange={handleItemChange} />
                </div>
                <div className="si-field">
                  <label>Amount</label>
                  <input type="number" value={itemForm.Amount} readOnly className="si-readonly" />
                </div>
              </div>
              <div className="si-item-entry-actions">
                <button className="si-tb-btn si-tb-save btn-sm" onClick={handleAddItem}>
                  {editingItemIdx !== null ? <><Save size={14} /> Update Item</> : <><Plus size={14} /> Add Item</>}
                </button>
                {editingItemIdx !== null && <button className="si-tb-btn btn-sm" onClick={handleCancelItem}>Cancel</button>}
              </div>
            </div>

            {invoiceItems.length > 0 && (
              <div className="si-items-table-wrap">
                <table className="si-table">
                  <thead>
                    <tr>
                      <th style={{ width:36 }}>#</th>
                      <th>Description</th>
                      <th className="si-th-right">Qty</th>
                      <th className="si-th-right">Unit Price</th>
                      <th className="si-th-right">Amount</th>
                      <th className="si-th-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, i) => (
                      <tr key={i} className="si-tr">
                        <td style={{ color:'#aaa', fontSize:12 }}>{i + 1}</td>
                        <td>{item.Description || '—'}</td>
                        <td className="si-td-right si-mono">{f2(item.Quantity)}</td>
                        <td className="si-td-right si-mono">{f2(item.UnitPrice)}</td>
                        <td className="si-td-right si-mono si-bold">{f2(item.Amount)}</td>
                        <td className="si-td-center">
                          <div className="si-row-actions">
                            <button className="si-action-btn si-action-edit"   onClick={() => handleEditItem(i)}   title="Edit"><Edit2  size={13} /></button>
                            <button className="si-action-btn si-action-delete" onClick={() => handleDeleteItem(i)} title="Delete"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Summary */}
          {invoiceItems.length > 0 && (
            <div className="si-section">
              <h3 className="si-section-title"><TrendingDown size={16} /> Invoice Summary</h3>
              <div className="si-summary-layout">
                <div className="si-summary-inputs">
                  <div className="si-field"><label>Discount %</label><input type="number" step="0.01" name="DiscountPercent" value={form.DiscountPercent} onChange={handleFormChange} placeholder="0.00" /></div>
                  <div className="si-field"><label>Tax %</label><input type="number" step="0.01" name="TaxPercent" value={form.TaxPercent} onChange={handleFormChange} placeholder="0.00" /></div>
                </div>
                <div className="si-totals-box">
                  <div className="si-total-row"><span>Sub Total</span>   <span className="si-mono">{f2(form.SubTotal)}</span></div>
                  <div className="si-total-row"><span>Discount</span>    <span className="si-mono">−{f2(form.DiscountAmount)}</span></div>
                  <div className="si-total-row"><span>Tax</span>         <span className="si-mono">+{f2(form.TaxAmount)}</span></div>
                  <div className="si-total-row si-grand-total"><span>Total Amount</span><span className="si-mono">{f2(form.TotalAmount)}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="si-section">
            <div className="si-field">
              <label>Notes</label>
              <textarea name="Notes" value={form.Notes || ''} onChange={handleFormChange} rows={3} placeholder="Additional notes about this supplier invoice…" />
            </div>
          </div>

        </div>{/* end body */}

        {/* FOOTER */}
        <div className="si-modal-footer">
          <button className="si-tb-btn" onClick={onClose} disabled={saving || generating}>Cancel</button>
          <button className="si-tb-btn si-tb-save" onClick={handleSave} disabled={saving || generating || !invoiceItems.length}>
            {saving ? <><span className="si-btn-spinner" /> Saving…</> : <><Save size={16} /> {editData ? 'Update Invoice' : 'Save Invoice'}</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SupplierInvoiceForm;